import { useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Place } from '@wingman/shared';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, CATEGORIES } from '@wingman/shared';
import { getCategoryIcon } from '../../lib/categoryIcons.js';
import { buildPopupHTML, getOpenStatus } from './MapPopup.js';
import VehicleMarker from './VehicleMarker.js';
import type { MapTheme } from '../../store/mapStore.js';
import type { TimeOfDayPhase } from '../../lib/timeOfDay.js';
import { useCommandStore } from '../../store/commandStore.js';

export type MapVisualMode = 'raw' | 'blueprint';

interface MapViewProps {
  places: Place[];
  selectedPlace: Place | null;
  activeCategory: string | null;
  onPlaceClick: (place: Place) => void;
  onMapMove: (bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number }, zoom: number) => void;
  routeGeometry?: GeoJSON.LineString | null;
  center?: { lat: number; lng: number };
  zoom?: number;
  visualMode?: MapVisualMode;
  mapTheme?: MapTheme;
  flyToTarget?: { lat: number; lng: number; zoom?: number } | null;
  onFlyToComplete?: () => void;
  highlightedPlaceId?: string | null;
  vehicleProgress?: number;
  /** Current time-of-day phase — drives the live map colour grade. */
  timePhase?: TimeOfDayPhase;
  /** Current weather icon key — nudges the map grade (rain cools, snow brightens). */
  weatherIcon?: string;
  /** Fired once with the live map instance so sibling layers can attach. */
  onMapReady?: (map: maplibregl.Map) => void;
}

const CATEGORY_SLUGS: Record<number, string> = CATEGORIES.reduce((acc, c) => {
  acc[c.id] = c.slug;
  return acc;
}, {} as Record<number, string>);

/**
 * Per-phase raster grade for the cyberpunk base map. The map canvas itself
 * shifts colour temperature and exposure with the time of day — dawn warms up,
 * night cools and darkens — so the surface reads as a living environment rather
 * than a static print. Values are MapLibre raster-paint properties.
 */
const PHASE_RASTER_GRADE: Record<TimeOfDayPhase, {
  hueRotate: number;
  brightnessMin: number;
  brightnessMax: number;
  saturation: number;
  contrast: number;
  opacity: number;
}> = {
  dawn:          { hueRotate: 25,  brightnessMin: 0.04, brightnessMax: 0.42, saturation: -0.7,  contrast: 0.45, opacity: 0.6 },
  day:           { hueRotate: 205, brightnessMin: 0.03, brightnessMax: 0.46, saturation: -0.8,  contrast: 0.5,  opacity: 0.5 },
  'golden-hour': { hueRotate: 20,  brightnessMin: 0.03, brightnessMax: 0.4,  saturation: -0.6,  contrast: 0.55, opacity: 0.58 },
  dusk:          { hueRotate: 250, brightnessMin: 0.02, brightnessMax: 0.34, saturation: -0.85, contrast: 0.6,  opacity: 0.55 },
  night:         { hueRotate: 210, brightnessMin: 0.0,  brightnessMax: 0.28, saturation: -1.0,  contrast: 0.7,  opacity: 0.55 },
};

/**
 * Weather modulation layered on top of the phase grade. Live conditions nudge
 * the base map: rain cools and dims, snow brightens, fog flattens contrast,
 * storms drop exposure. Applied multiplicatively/additively to the phase base.
 */
const WEATHER_RASTER_MOD: Record<string, {
  hueShift?: number;
  brightnessDelta?: number;
  saturationDelta?: number;
  contrastDelta?: number;
}> = {
  rain:  { hueShift: 15,  brightnessDelta: -0.06, saturationDelta: -0.1, contrastDelta: -0.1 },
  storm: { hueShift: 20,  brightnessDelta: -0.1,  saturationDelta: -0.15, contrastDelta: -0.15 },
  snow:  { hueShift: -10, brightnessDelta: 0.08,  saturationDelta: -0.2, contrastDelta: -0.1 },
  fog:   { brightnessDelta: 0.02, saturationDelta: -0.25, contrastDelta: -0.25 },
  cloud: { brightnessDelta: -0.03, saturationDelta: -0.05 },
  partly:{ brightnessDelta: -0.01 },
};

function buildCyberpunkRasterPaint(
  visualMode: MapVisualMode,
  phase: TimeOfDayPhase,
  weatherIcon?: string,
): Record<string, number> {
  const raw = visualMode === 'raw';
  const grade = PHASE_RASTER_GRADE[phase] ?? PHASE_RASTER_GRADE.night;
  const mod = (weatherIcon && WEATHER_RASTER_MOD[weatherIcon]) || {};

  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  // RAW keeps its high-contrast ops look; blueprint is softer. The phase grade
  // drives the base, weather nudges it.
  const baseBrightnessMax = raw ? grade.brightnessMax : grade.brightnessMax + 0.08;
  const baseContrast = raw ? grade.contrast : grade.contrast - 0.15;

  return {
    'raster-opacity': grade.opacity,
    'raster-saturation': clamp(grade.saturation + (mod.saturationDelta ?? 0), -1, 1),
    'raster-contrast': clamp(baseContrast + (mod.contrastDelta ?? 0), 0, 1),
    'raster-brightness-min': clamp(grade.brightnessMin + (mod.brightnessDelta ?? 0), 0, 1),
    'raster-brightness-max': clamp(baseBrightnessMax + (mod.brightnessDelta ?? 0), 0, 1),
    'raster-hue-rotate': (grade.hueRotate + (mod.hueShift ?? 0)) % 360,
  };
}

function getMapStyle(
  tileserverUrl: string,
  tileType: string,
  visualMode: MapVisualMode,
  theme: MapTheme,
  phase: TimeOfDayPhase = 'night',
) {
  if (tileType === 'vector' && tileserverUrl) {
    const stylePath = visualMode === 'raw' ? '/styles/dark.json' : '/styles/default.json';
    return `${tileserverUrl}${stylePath}`;
  }
  const hasTileserver = tileserverUrl && !tileserverUrl.startsWith('http://localhost');
  const tiles = hasTileserver
    ? [`${tileserverUrl}/data/{z}/{x}/{y}.png`]
    : ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'];
  const isCyberpunk = theme === 'cyberpunk';

  if (!isCyberpunk) {
    return {
      version: 8 as const,
      sources: {
        'osm-tiles': {
          type: 'raster' as const,
          tiles,
          tileSize: 256,
          attribution: '&copy; OpenStreetMap contributors',
        },
      },
      layers: [
        { id: 'system-background', type: 'background' as const, paint: { 'background-color': '#f0f0f0' } },
        { id: 'osm-layer', type: 'raster' as const, source: 'osm-tiles', paint: { 'raster-opacity': 0.95, 'raster-saturation': -0.1, 'raster-contrast': 0.05 } },
      ],
    } as maplibregl.StyleSpecification;
  }

  // Palantir-style dark intelligence base map
  return {
    version: 8 as const,
    sources: {
      'osm-tiles': {
        type: 'raster' as const,
        tiles,
        tileSize: 256,
        attribution: '&copy; OpenStreetMap contributors',
      },
    },
    layers: [
      // Deep charcoal base
      { id: 'system-background', type: 'background' as const, paint: { 'background-color': '#0A0C10' } },
      {
        id: 'osm-layer',
        type: 'raster' as const,
        source: 'osm-tiles',
        paint: buildCyberpunkRasterPaint(visualMode, phase),
      },
    ],
  } as maplibregl.StyleSpecification;
}

function escapeHtml(str: string): string {
  return str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function buildMarkerElement(place: Place, isActive: boolean, isDimmed: boolean, isSelected: boolean, showLabel: boolean): HTMLElement {
  const cat = CATEGORIES.find((c) => c.id === place.categoryId);
  const color = cat?.color || '#00f5ff';
  const slug = cat?.slug || '';
  const Icon = getCategoryIcon(slug);
  const status = getOpenStatus(place);
  const iconSvg = renderToStaticMarkup(<Icon size={16} color={color} />);

  const el = document.createElement('div');
  el.className = 'wingman-marker';
  if (status === 'open') el.classList.add('is-open');
  if (status === 'closed') el.classList.add('is-closed');
  if (isDimmed) el.classList.add('is-dimmed');
  if (isActive && !isDimmed) el.classList.add('is-highlighted');
  if (isSelected) el.classList.add('is-selected');
  if (showLabel) el.classList.add('show-label');

  el.innerHTML = `
    <div class="wingman-marker-icon" style="border-color: ${color};">
      ${iconSvg}
    </div>
    <div class="wingman-marker-label">${escapeHtml(place.name)}</div>
  `;

  return el;
}

export default function MapView({
  places,
  selectedPlace,
  activeCategory,
  onPlaceClick,
  onMapMove,
  routeGeometry,
  center,
  zoom,
  visualMode = 'raw',
  mapTheme = 'cyberpunk',
  flyToTarget,
  onFlyToComplete,
  highlightedPlaceId,
  vehicleProgress = 0,
  timePhase = 'night',
  weatherIcon,
  onMapReady,
}: MapViewProps) {
  const [mapError, setMapError] = useState<string | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const placesRef = useRef<Place[]>(places);
  const activeCategoryRef = useRef<string | null>(activeCategory);
  const selectedPlaceRef = useRef<Place | null>(selectedPlace);
  const onMapReadyRef = useRef(onMapReady);
  onMapReadyRef.current = onMapReady;

  placesRef.current = places;
  activeCategoryRef.current = activeCategory;
  selectedPlaceRef.current = selectedPlace;

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const tileserverUrl = import.meta.env.VITE_TILESERVER_URL || '';
    const tileType = import.meta.env.VITE_TILE_TYPE || 'raster';
    const style = getMapStyle(tileserverUrl, tileType, visualMode, mapTheme, timePhase);

    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: mapContainer.current,
        style,
        center: [center?.lng || DEFAULT_MAP_CENTER.lng, center?.lat || DEFAULT_MAP_CENTER.lat],
        zoom: zoom || DEFAULT_MAP_ZOOM,
      });
    } catch (err) {
      console.error('[MapView] Failed to initialize map:', err);
      setMapError('Unable to initialize map renderer (WebGL unavailable).');
      return;
    }

    map.addControl(new maplibregl.NavigationControl(), 'bottom-right');
    map.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      'bottom-right',
    );

    map.on('moveend', () => {
      const bounds = map.getBounds();
      onMapMove({
        minLng: bounds.getWest(),
        minLat: bounds.getSouth(),
        maxLng: bounds.getEast(),
        maxLat: bounds.getNorth(),
      }, map.getZoom());
    });

    map.on('load', () => {
      map.addSource('places', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 14,
      });

      // Cluster layer (visible at low zoom)
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'places',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#58A6FF',
          'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 32],
          'circle-opacity': 0.12,
          'circle-stroke-width': 2,
          'circle-stroke-color': 'rgba(88,166,255,0.45)',
        },
      });

      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'places',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 13,
          'text-font': ['Noto Sans Mono Regular', 'Noto Sans Regular'],
        },
        paint: {
          'text-color': '#58A6FF',
        },
      });

      // Invisible layer for unclustered points (for click detection)
      map.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'places',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 0,
          'circle-opacity': 0,
        },
      });

      // Cluster click → expand
      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        const clusterId = features[0]?.properties?.cluster_id;
        if (clusterId === undefined) return;
        const source = map.getSource('places') as maplibregl.GeoJSONSource;
        source.getClusterExpansionZoom(clusterId).then((zoom: number) => {
          const coords = (features[0].geometry as GeoJSON.Point).coordinates;
          map.flyTo({ center: coords as [number, number], zoom, essential: true });
        });
      });

      // ── Activity Heatmap Layer (density of places / "what's happening") ──
      map.addLayer({
        id: 'activity-heat',
        type: 'heatmap',
        source: 'places',
        maxzoom: 15,
        paint: {
          'heatmap-weight': [
            'interpolate', ['linear'], ['coalesce', ['get', 'weight'], 1],
            0, 0,
            1, 1,
          ],
          'heatmap-intensity': [
            'interpolate', ['linear'], ['zoom'],
            10, 1,
            15, 3,
          ],
          'heatmap-radius': [
            'interpolate', ['linear'], ['zoom'],
            10, 20,
            15, 40,
          ],
          'heatmap-opacity': 0.55,
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(0, 245, 255, 0)',
            0.2, 'rgba(0, 245, 255, 0.35)',
            0.4, 'rgba(45, 212, 191, 0.5)',
            0.6, 'rgba(250, 204, 21, 0.6)',
            0.8, 'rgba(251, 146, 60, 0.75)',
            1, 'rgba(248, 81, 73, 0.85)',
          ],
        },
        layout: { visibility: 'none' },
      });

      map.on('mouseenter', 'clusters', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'clusters', () => {
        map.getCanvas().style.cursor = '';
      });
    });

    mapRef.current = map;

    // Let sibling layers (events, etc.) attach to the live map instance.
    onMapReadyRef.current?.(map);

    const bounds = map.getBounds();
    onMapMove({
      minLng: bounds.getWest(),
      minLat: bounds.getSouth(),
      maxLng: bounds.getEast(),
      maxLat: bounds.getNorth(),
    }, map.getZoom());

    return () => {
      popupRef.current?.remove();
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Update theme class on container
  useEffect(() => {
    const container = mapContainer.current;
    if (!container) return;
    container.classList.remove('map-theme-cyberpunk', 'map-theme-conventional');
    container.classList.add(`map-theme-${mapTheme}`);
  }, [mapTheme]);

  // Live re-grade: as the time-of-day phase or weather changes, re-tint the
  // base map raster in place — no re-init, no flicker. Only the cyberpunk
  // theme is graded; conventional keeps its neutral daylight look.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mapTheme !== 'cyberpunk' || !map.getLayer('osm-layer')) return;
    const paint = buildCyberpunkRasterPaint(visualMode, timePhase, weatherIcon);
    for (const [prop, value] of Object.entries(paint)) {
      map.setPaintProperty('osm-layer', prop, value);
    }
  }, [timePhase, weatherIcon, visualMode, mapTheme]);

  // Toggle heatmap layer based on command store
  const heatmapVisible = useCommandStore((s) => s.layerVisibility.heatmap);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('activity-heat')) return;
    map.setLayoutProperty(
      'activity-heat',
      'visibility',
      heatmapVisible ? 'visible' : 'none',
    );
  }, [heatmapVisible]);

  // Sync markers when places/filter/selection change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('places')) return;

    const currentPlaces = placesRef.current;
    const currentCategory = activeCategoryRef.current;
    const currentSelected = selectedPlaceRef.current;
    const showLabels = map.getZoom() > 14;

    // Update GeoJSON source (for clustering)
    const features: GeoJSON.Feature<GeoJSON.Point>[] = currentPlaces.map((p) => {
      // Weight for heatmap: rating (if available) + open bonus = "activity level"
      const ratingWeight = (p.rating && p.rating > 0) ? Math.min(p.rating / 5, 1) : 0.3;
      const openBonus = p.freshnessStatus === 'verified' ? 0.2 : 0;
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        properties: {
          id: p.id,
          name: p.name,
          categoryId: p.categoryId,
          weight: Math.min(ratingWeight + openBonus, 1),
          rating: p.rating || 0,
          open: getOpenStatus(p),
        },
      };
    });

    (map.getSource('places') as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features,
    });

    // Manage individual markers for unclustered points
    const visibleIds = new Set<string>();
    const currentZoom = map.getZoom();

    for (const place of currentPlaces) {
      // Skip if would be clustered
      if (currentZoom < 14) continue;

      visibleIds.add(place.id);
      const existing = markersRef.current.get(place.id);

      const catSlug = CATEGORY_SLUGS[place.categoryId] || '';
      const isActive = !currentCategory || catSlug === currentCategory;
      const isDimmed = currentCategory !== null && catSlug !== currentCategory;
      const isSelected = currentSelected?.id === place.id;

      if (existing) {
        const el = existing.getElement();
        el.classList.toggle('is-dimmed', isDimmed);
        el.classList.toggle('is-highlighted', isActive && !isDimmed);
        el.classList.toggle('is-selected', isSelected);
        el.classList.toggle('show-label', showLabels);
      } else {
        const el = buildMarkerElement(place, isActive, isDimmed, isSelected, showLabels);
        const marker = new maplibregl.Marker({ element: el, anchor: 'bottom' })
          .setLngLat([place.lng, place.lat])
          .addTo(map);

        // Two-step Google Maps flow:
        // Click marker → show compact info popup
        // Click "View details" in popup → open full panel
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          popupRef.current?.remove();

          const currentPlace = placesRef.current.find((p) => p.id === place.id) || place;
          const ratingStr = currentPlace.rating && currentPlace.rating > 0
            ? `<span class="mip-rating">${currentPlace.rating.toFixed(1)} ★</span>`
            : '';
          const statusColor = getOpenStatus(currentPlace) === 'open' ? 'var(--green)' : getOpenStatus(currentPlace) === 'closed' ? 'var(--red)' : 'var(--text-dim)';
          const statusLabel = getOpenStatus(currentPlace) === 'open' ? 'Open now' : getOpenStatus(currentPlace) === 'closed' ? 'Closed' : '';

          const html = `
            <div class="map-info-popup">
              <div class="mip-header">
                <div class="mip-name">${escapeHtml(currentPlace.name)}</div>
                ${ratingStr}
              </div>
              <div class="mip-meta">
                ${currentPlace.categoryName ? `<span class="mip-category">${escapeHtml(currentPlace.categoryName)}</span>` : ''}
                ${statusLabel ? `<span class="mip-status" style="color:${statusColor}">${statusLabel}</span>` : ''}
              </div>
              ${currentPlace.address ? `<div class="mip-address">${escapeHtml(currentPlace.address)}</div>` : ''}
              <button class="mip-cta" data-place-id="${escapeHtml(currentPlace.id)}">View details</button>
            </div>
          `;

          const popup = new maplibregl.Popup({
            closeButton: true,
            closeOnClick: true,
            offset: 16,
            anchor: 'bottom',
            className: 'wingman-popup',
          })
            .setLngLat([currentPlace.lng, currentPlace.lat])
            .setHTML(html)
            .addTo(map);

          // Wire the CTA button
          setTimeout(() => {
            const btn = document.querySelector(`.mip-cta[data-place-id="${currentPlace.id}"]`) as HTMLButtonElement | null;
            if (btn) btn.onclick = () => { popup.remove(); onPlaceClick(currentPlace); };
          }, 0);

          popupRef.current = popup;
        });

        el.addEventListener('mouseenter', () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        el.addEventListener('mouseleave', () => {
          map.getCanvas().style.cursor = '';
        });

        markersRef.current.set(place.id, marker);
      }
    }

    // Remove markers for places no longer visible
    markersRef.current.forEach((marker, id) => {
      if (!visibleIds.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
      }
    });
  }, [places, activeCategory, selectedPlace, mapTheme, onPlaceClick]);

  // Update label visibility on zoom change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onZoom = () => {
      const showLabels = map.getZoom() > 14;
      markersRef.current.forEach((marker) => {
        marker.getElement().classList.toggle('show-label', showLabels);
      });

      // Remove markers when zooming out below 14 (they'll be clustered)
      if (map.getZoom() < 14) {
        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current.clear();
      }
    };

    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, []);

  // Fly to target
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyToTarget) return;

    map.flyTo({
      center: [flyToTarget.lng, flyToTarget.lat],
      zoom: flyToTarget.zoom || 16,
      essential: true,
      duration: 1200,
    });

    const onComplete = () => {
      onFlyToComplete?.();
      map.off('moveend', onComplete);
    };
    map.on('moveend', onComplete);
  }, [flyToTarget, onFlyToComplete]);

  // Route geometry
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (routeGeometry) {
      if (map.getSource('route')) {
        (map.getSource('route') as maplibregl.GeoJSONSource).setData({
          type: 'Feature',
          properties: {},
          geometry: routeGeometry,
        });
      } else {
        map.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: routeGeometry,
          },
        });
        map.addLayer({
          id: 'route-casing',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': 'rgba(255,255,255,0.92)', 'line-width': 11, 'line-opacity': 0.88 },
        });
        map.addLayer({
          id: 'route-layer',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#0f82f2',
            'line-width': 6,
            'line-opacity': 0.94,
            'line-gradient': ['interpolate', ['linear'], ['line-progress'], 0, '#8b5cf6', 0.35, '#228be6', 1, '#74c0fc'],
            'line-dasharray': [1.4, 1.2],
          },
        });
      }
    } else {
      if (map.getLayer('route-layer')) map.removeLayer('route-layer');
      if (map.getLayer('route-casing')) map.removeLayer('route-casing');
      if (map.getSource('route')) map.removeSource('route');
    }
  }, [routeGeometry]);

  // Center/zoom changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !center) return;
    map.flyTo({
      center: [center.lng, center.lat],
      zoom: zoom || map.getZoom(),
      essential: true,
    });
  }, [center, zoom]);

  if (mapError) {
    return (
      <div
        id="map-canvas"
        className={`map-container map-mode-${visualMode} map-theme-${mapTheme} flex items-center justify-center`}
        aria-label="Map view"
        role="application"
        style={{ flexDirection: 'column', gap: 12, color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}
      >
        <div style={{ fontSize: 32 }}>🗺️</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Map unavailable</div>
        <div style={{ fontSize: 12, maxWidth: 280 }}>{mapError}</div>
        <button
          type="button"
          onClick={() => { setMapError(null); window.location.reload(); }}
          style={{ marginTop: 8, padding: '6px 16px', border: '1px solid var(--accent)', borderRadius: 4, background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        ref={mapContainer}
        id="map-canvas"
        className={`map-container map-mode-${visualMode} map-theme-${mapTheme}`}
        aria-label="Map view"
        role="application"
      />
      {mapRef.current && routeGeometry && vehicleProgress > 0 && (
        <VehicleMarker
          map={mapRef.current}
          routeGeometry={routeGeometry}
          progress={vehicleProgress}
          visible={vehicleProgress > 0}
        />
      )}
    </>
  );
}
